import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SidebarState {
  activeModule: string;
  collapsed: boolean;
  isLoading: boolean;
  setActiveModule: (module: string) => void;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  setLoading: (loading: boolean) => void;
}

export const useSidebarStore = create<SidebarState>((set) => ({
  activeModule: 'dashboard',
  collapsed: false,
  isLoading: true, // Start with loading state
  setActiveModule: (module) => set({ activeModule: module }),
  setCollapsed: (collapsed) => set({ collapsed }),
  toggleCollapsed: () => set((state) => ({ collapsed: !state.collapsed })),
  setLoading: (loading) => set({ isLoading: loading }),
}));

interface AuthState {
  user: {
    id: string;
    email: string;
    name: string;
    roles?: string[];
    groups?: string[];
    company_display_name?: string;
    full_name?: string;
    company_id?: string;
    companyId?: string;
    countries?: string[];
    phone_number?: string;
    access_token_age?: number;
    refresh_token_age?: number;
    succeeded?: boolean;
    user_info?: any;
    error_message?: string;
    refresh_token?: string;
    product_permissions?: Record<string, boolean>;
  } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  productAccess: Record<string, boolean>;
  checkingProductAccess: Record<string, boolean>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthState['user']) => void;
  checkProductAccess: (productName: string) => Promise<boolean>;
  callGptApi: () => Promise<string>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  productAccess: {},
  checkingProductAccess: {},
  login: async (email: string, password: string) => {
    console.log('[STORE] login called with email:', email);
    set({ isLoading: true });
    try {
      // Use the new dynamic realm authentication
      const { loginWithCredentials } = await import('@/lib/auth');
      
      console.log('[STORE] Calling loginWithCredentials...');
      const responseData = await loginWithCredentials(email, password);
      console.log('[STORE] loginWithCredentials response received:', {
        succeeded: responseData.succeeded,
        has_user_info: !!responseData.user_info,
        error_message: responseData.error_message,
      });
      
      // Check if login was successful based on the API response structure
      if (!responseData.succeeded) {
        console.error('[STORE] Login failed - responseData.succeeded is false');
        // Use the error_message from the API response if available
        const errorMessage = responseData.error_message || 'Login failed';
        
        // Parse the error message to provide user-friendly feedback
        if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          throw new Error('Network error. Please check your internet connection and try again.');
        } else {
          throw new Error(errorMessage);
        }
      }

      console.log('[STORE] Login succeeded, creating user object...');
      // Tokens are stored in httpOnly cookies by the backend
      // Frontend does NOT store tokens in localStorage for security
      // Backend sets cookies automatically via set_auth_cookies in login response

      // Create user object from authentication response
      const user = {
        id: responseData.user_info?.id,
        email: responseData.user_info?.email,
        name: responseData.user_info?.full_name,
        full_name: responseData.user_info?.full_name,
        company_display_name: responseData.user_info?.company_display_name,
        company_id: responseData.user_info?.company_id,
        roles: responseData.user_info?.roles || [],
        groups: responseData.user_info?.groups || [],
        countries: responseData.user_info?.countries,
        phone_number: responseData.user_info?.phone_number,
        access_token_age: responseData.access_token_age,
        refresh_token_age: responseData.refresh_token_age,
        succeeded: responseData.succeeded,
        user_info: responseData.user_info,
        error_message: responseData.error_message,
        refresh_token: responseData.refresh_token,
        product_permissions: responseData.user_info?.product_permissions,
      };
      console.log('[STORE] User object created:', {
        id: user.id,
        email: user.email,
        company_id: user.company_id,
      });
      
      // Set authenticated user
      console.log('[STORE] Setting store state - isAuthenticated: true');
      set({ user, isAuthenticated: true, isLoading: false });
      console.log('[STORE] Store state updated');

      // One-time UI language default from primary country (does not override after user choice / persistence).
      const userCountry =
        user.countries && user.countries.length > 0 ? user.countries[0].toLowerCase() : '';
      if (typeof window !== 'undefined') {
        try {
          const ONBOARD_KEY = 'snapland-lang-onboarding-v1';
          const USER_CHOSE_KEY = 'snapland-user-chose-language';
          if (!localStorage.getItem(ONBOARD_KEY)) {
            if (!localStorage.getItem(USER_CHOSE_KEY)) {
              const { setLanguage } = useLanguageStore.getState();
              const def =
                userCountry === 'israel' ? 'he' : userCountry === 'spain' ? 'es' : 'en';
              setLanguage(def);
              void import('./i18n').then((m) => m.default.changeLanguage(def));
            }
            localStorage.setItem(ONBOARD_KEY, '1');
          }
        } catch {
          // ignore storage / i18n errors
        }
      }

      // Set user's country in the country store
      const { setCountry, setAvailableCountries } = useCountryStore.getState();
      console.log('[STORE] Setting user country:', userCountry, 'available:', user.countries);
      if (userCountry) {
        setCountry(userCountry);
      }
      const userAvailableCountries = (user.countries ?? [])
        .map((c: string) => c.toLowerCase())
        .filter((c: string) => !!c);
      if (userAvailableCountries.length > 0) {
        setAvailableCountries(userAvailableCountries);
      }

      // Load realm tags for this user (and print what was received)
      try {
        const { useRealmTagsStore } = await import('@/lib/realmTagsStore');
        await useRealmTagsStore.getState().loadForUser(user as any, userCountry);
      } catch (e) {
        console.warn('Unable to load realm tags after login:', e);
      }
    } catch (error) {
      console.error('[STORE] Login error:', error);
      set({ isLoading: false });
      throw error;
    }
  },
  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
  },
  checkProductAccess: async (productName: string) => {
    const state = get();
    const user = state.user;
    
    if (!user || !user.id) {
      return false;
    }
    
    // Check if we already have the result cached
    if (productName in state.productAccess) {
      // // console.log('Product access cached:', state.productAccess[productName]);
      return state.productAccess[productName];
    }
    
    // Check against the user's permissions object received during login
    if (user.product_permissions) {
      
      // Handle permissions as object (key-value pairs)
      const permissionsObject = user.product_permissions;
      const productKey = productName.toLowerCase();
      
      // Check if the product exists as a key in the permissions object
      const hasAccess = permissionsObject.hasOwnProperty(productKey) && permissionsObject[productKey] === true;
      // // console.log('Access result from permissions object:', hasAccess);
      
      // Cache the result
      set((state) => ({
        productAccess: {
          ...state.productAccess,
          [productName]: hasAccess
        }
      }));
      
      return hasAccess;
    }
    
    // If no permissions array is available, return false
    console.warn('No product permissions array found for user');
    return false;
  },
  logout: async () => {
    // Call logout endpoint - backend will clear httpOnly cookies
    try {
      const { logout } = await import('@/lib/auth');
      // For httpOnly cookies, we can't read refresh token from client
      // Backend will read it from cookies
      await logout();
    } catch (error) {
      console.error('Server logout failed:', error);
      // Continue with local logout even if server logout fails
    }
    
    // Clear all authentication data
    set({ user: null, isAuthenticated: false, productAccess: {}, checkingProductAccess: {} });

    // Clear realm tags store on logout
    try {
      const { useRealmTagsStore } = await import('@/lib/realmTagsStore');
      useRealmTagsStore.getState().clear();
    } catch (e) {
      // no-op
    }
    
    // Clear GPT token store on logout
    try {
      const { useGptTokenStore } = await import('@/lib/gptTokenStore');
      useGptTokenStore.getState().clear();
    } catch (e) {
      // no-op
    }

    // Clear Executive Summary persisted state on logout (prevents cross-user leakage on shared browsers)
    try {
      const { useExecutiveSummaryStore } = await import('@/lib/executiveSummaryStore');
      useExecutiveSummaryStore.getState().clearAll();
    } catch (e) {
      // no-op
    }
    
    // Clear sessionStorage to ensure GPT token is removed
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('gpt-token-store');
    }

    // Clear localStorage persisted executive summary tasks
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('executive-summary-storage');
      } catch {
        // no-op
      }
    }
    
    // Note: Cannot clear httpOnly cookies from client-side
    // Backend should handle cookie clearing via logout endpoint
    
    // Clear any cached tokens from clientTokenUtils
    if (typeof window !== 'undefined') {
      import('@/lib/clientTokenUtils').then(({ clearTokenCache }) => {
        clearTokenCache();
      });
    }
    if (!window.location.pathname.includes('/login')) {
      setTimeout(() => {
        window.location.replace('/login');
      }, 150);
    }
  },
  callGptApi: async () => {
    const state = get();

    if (!state.user) {
      throw new Error('User not authenticated');
    }
    
    try {
      // Use the AI proxy API route which handles authentication with cookies
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      const response = await fetch('/api/ai-proxy?endpoint=gpt', {
        method: 'POST',
        body: JSON.stringify({}),
        headers,
        credentials: 'include',
      });
      
      const responseText = await response.text();

      // Successful response is a plain text URL (not JSON)
      if (!response.ok) {
        // Try to surface the backend error message if it's JSON
        try {
          const maybeJson = JSON.parse(responseText);
          const detail = maybeJson?.detail ?? maybeJson?.error ?? responseText;
          throw new Error(String(detail));
        } catch {
          throw new Error(responseText || `GPT API error: ${response.status}`);
        }
      }

      return responseText;
    } catch (error) {
      console.error('GPT API Error:', error);
      throw error;
    }
  },
}),
{
  name: 'auth-storage',
  partialize: (state) => ({
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    productAccess: state.productAccess,
  }),
  skipHydration: true,
}
  ) 
  
);


// Language store
interface LanguageState {
  currentLanguage: 'en' | 'es' | 'he';
  setLanguage: (language: 'en' | 'es' | 'he') => void;
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      currentLanguage: 'en',
      setLanguage: (language) => set({ currentLanguage: language }),
    }),
    {
      name: 'language-storage',
      skipHydration: true,
    }
  )
);

// Country store
interface CountryState {
  currentCountry: string;
  setCountry: (country: string) => void;
  availableCountries: string[];
  setAvailableCountries: (countries: string[]) => void;
}

export const useCountryStore = create<CountryState>()(
  persist(
    (set) => ({
      currentCountry: '',
      availableCountries: [],
      setCountry: (country: string) => {
        if (country) set({ currentCountry: country });
      },
      setAvailableCountries: (countries: string[]) => {
        const cleaned = (countries ?? []).filter((c) => !!c);
        set({
          availableCountries: cleaned,
          currentCountry: cleaned.length > 0 ? cleaned[0] : '',
        });
      },
    }),
    {
      name: 'country-storage',
      skipHydration: true,
      // Discard any historically-persisted `undefined`/non-string
      // `currentCountry`; a missing country was being forwarded to the
      // realm-tags Server Action as `["$undefined"]`, which caused an
      // infinite fetch loop on /properties. We keep the store empty until a
      // real country is set so callers must gate on it being truthy.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<CountryState>;
        return {
          ...current,
          ...p,
          currentCountry:
            typeof p.currentCountry === 'string' && p.currentCountry
              ? p.currentCountry
              : current.currentCountry,
          availableCountries:
            Array.isArray(p.availableCountries) && p.availableCountries.length
              ? p.availableCountries.filter((c) => typeof c === 'string' && !!c)
              : current.availableCountries,
        };
      },
    }
  )
);

